import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert, Box, Button, Collapse, Divider, IconButton, InputAdornment, Link, ListSubheader, Menu, MenuItem, Select, Stack,
  TextField, Tooltip, Typography, alpha,
} from '@mui/material';
import {
  CloseRounded as CloseIcon, DeleteOutlineRounded as RemoveIcon, AddRounded as AddIcon, EditOutlined as EditIcon,
  CheckRounded as DoneIcon, OpenInNewRounded as OpenIcon, UndoRounded as UndoIcon,
  ExpandMoreRounded as ExpandIcon, ForwardRounded as ForwardIcon, PushPinOutlined as PinOutlineIcon,
  PushPinRounded as PinIcon,
} from '@mui/icons-material';
import PayrollService, {
  AdjustmentKind, LineDay, PayrollLine, PayrollRun, RunTotals, describeError,
} from '../../api/services/payroll.service';
import ToneChip from '../../components/ui/ToneChip';
import {
  ADJUSTMENT_LABEL, Avatar, Eyebrow, STATUS_LABEL, count, money,
} from './payrollUI';

// One person's week, opened beside the table.
//
// In the order somebody reads it: who, the week's facts (days, stops,
// packages, training), the calculation line by line ending in the
// total, then the days behind it. The facts are edited in place — press
// the pencil and the same numbers become fields where they stand, so
// nothing moves — and a typed number keeps the statement's beside it
// with a small Reset.

const WEEKDAY_INDEX = ['Sat', 'Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri'];

// A calculated line can come off one week (an exception): it stays here,
// struck through and unpaid, with a way back. The person's pay rules are
// not touched, so next week pays it as usual.
const Receipt: React.FC<{
  line: PayrollLine;
  locked: boolean;
  onRemove: (id: string) => void;
  onSkipRule: (ruleId: string, skip: boolean) => void;
}> = ({ line, locked, onRemove, onSkipRule }) => (
  <Stack divider={<Divider sx={{ borderStyle: 'dashed' }} />}>
    {line.receipt.map((r, i) => (
      <Stack key={`${r.ruleId}-${i}`} direction="row" justifyContent="space-between" alignItems="center" gap={1.5} sx={{ py: 1 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, ...(r.skipped && { color: 'text.disabled', textDecoration: 'line-through' }) }}>{r.label}</Typography>
          <Typography variant="caption" sx={{ color: r.incomplete ? 'warning.dark' : 'text.secondary', fontWeight: r.incomplete ? 600 : 400 }}>
            {r.detail}
          </Typography>
        </Box>
        <Stack direction="row" alignItems="center" gap={0.5}>
          <Typography variant="body2" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', color: r.incomplete || r.amount === 0 ? 'text.disabled' : 'text.primary' }}>
            {r.incomplete || r.skipped ? '—' : money(r.amount, { cents: true })}
          </Typography>
          {!locked && r.ruleId && (r.skipped ? (
            <Tooltip title="Put back for this week">
              <IconButton size="small" aria-label={`Put ${r.label.toLowerCase()} back`} onClick={() => onSkipRule(r.ruleId, false)}>
                <UndoIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title="Remove for this week only. Pay rules stay the same.">
              <IconButton size="small" aria-label={`Remove ${r.label.toLowerCase()} this week`} onClick={() => onSkipRule(r.ruleId, true)}>
                <RemoveIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ))}
        </Stack>
      </Stack>
    ))}
    {line.adjustments.map((a) => (
      <Stack key={a._id} direction="row" justifyContent="space-between" alignItems="center" gap={1.5} sx={{ py: 1 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>{ADJUSTMENT_LABEL[a.kind]}</Typography>
          <Typography variant="caption" color="text.secondary">
            {[a.note, a.byName].filter(Boolean).join(' · ')}
          </Typography>
        </Box>
        <Stack direction="row" alignItems="center" gap={0.5}>
          <Typography variant="body2" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
            {money(a.amount, { cents: true })}
          </Typography>
          {!locked && (
            <Tooltip title="Remove">
              <IconButton size="small" aria-label={`Remove ${ADJUSTMENT_LABEL[a.kind].toLowerCase()}`} onClick={() => onRemove(a._id)}>
                <RemoveIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </Stack>
    ))}
    {line.receipt.length === 0 && line.adjustments.length === 0 && (
      <Typography variant="caption" color="text.secondary" sx={{ py: 1 }}>Nothing yet</Typography>
    )}
  </Stack>
);


const WEEKDAY_FULL = ['Saturday', 'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];

// Stops by day as rows: a weekday, a bar across the card, the number.
// Columns of near-equal bars read as "all the same"; a row per day with
// the threshold marked lets the eye compare lengths, which it is good at.
//
// Each day can be given to somebody else on the week (the forward
// button): drivers sometimes run a route under another driver's FedEx
// ID, and the day, its stops and its pay belong to who drove it. A day
// moved here says where it came from; a day moved away says who has it.
// Either can be undone from the row.
const dayKey = (iso: string) => iso.slice(0, 10);

const DayRows: React.FC<{
  line: PayrollLine;
  weekLines: PayrollLine[];
  weekStart: string;
  threshold: number | null;
  locked: boolean;
  busy: boolean;
  onMove: (fromLineId: string, date: string, toLineId: string) => void;
}> = ({ line, weekLines, weekStart, threshold, locked, busy, onMove }) => {
  const [menu, setMenu] = useState<{ anchor: HTMLElement; date: string } | null>(null);
  const indexOf = (iso: string) => Math.round((new Date(iso).getTime() - new Date(weekStart).getTime()) / 86400000);
  const byIndex = new Map<number, LineDay>();
  for (const d of line.days) byIndex.set(indexOf(d.date), d);
  // Days scanned under this person's ID that somebody else drove.
  const away = new Map<number, { line: PayrollLine; day: LineDay }>();
  for (const other of weekLines) {
    if (other._id === line._id) continue;
    for (const d of other.days) if (d.movedFrom?.lineId === line._id) away.set(indexOf(d.date), { line: other, day: d });
  }
  const start = new Date(weekStart);
  const dateOf = (i: number) => {
    const d = new Date(start.getTime() + i * 86400000);
    return `${d.getUTCMonth() + 1}/${d.getUTCDate()}`;
  };
  const max = Math.max(threshold || 0, ...line.days.map((d) => d.stops), 1);
  const nameOf = (l: PayrollLine) => l.name || l.statementName;
  const candidates = menu
    ? weekLines
      .filter((l) => l._id !== line._id && (l.accountId || l.custom) && !l.days.some((d) => dayKey(d.date) === dayKey(menu.date)))
      .sort((a, b) => nameOf(a).localeCompare(nameOf(b)))
    : [];
  const actionSx = { p: 0.5, color: 'text.secondary', '&:hover': { color: 'primary.main' } } as const;
  return (
    <Stack gap={0.5}>
      {WEEKDAY_INDEX.map((w, i) => {
        const day = byIndex.get(i);
        const stops = day?.stops;
        const gone = away.get(i);
        const over = threshold !== null && stops !== undefined && stops > threshold;
        return (
          <Box key={w}>
            <Stack direction="row" alignItems="center" gap={1.25} aria-label={`${WEEKDAY_FULL[i]} ${dateOf(i)}: ${stops === undefined ? 'off' : `${stops} stops`}`}>
              <Typography variant="caption" sx={{ width: 64, color: stops === undefined ? 'text.disabled' : 'text.secondary', flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>{w} {dateOf(i)}</Typography>
              <Box sx={{ flex: 1, height: 10, bgcolor: 'grey.100', borderRadius: 5, position: 'relative', overflow: 'hidden' }}>
                {stops !== undefined && (
                  <Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${(stops / max) * 100}%`, bgcolor: over ? 'primary.dark' : 'primary.main', borderRadius: 5, opacity: day?.movedFrom ? 0.6 : 1 }} />
                )}
                {threshold !== null && threshold <= max && (
                  <Box sx={{ position: 'absolute', top: -1, bottom: -1, left: `${(threshold / max) * 100}%`, borderLeft: '2px solid', borderColor: 'grey.500' }} />
                )}
              </Box>
              <Typography variant="body2" sx={{ width: 36, textAlign: 'right', fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: stops === undefined ? 'text.disabled' : 'text.primary', flexShrink: 0 }}>
                {stops === undefined ? '–' : stops}
              </Typography>
              {!locked && (
                <Box sx={{ width: 28, flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
                  {day && (
                    <Tooltip title="Driven by somebody else? Move this day to them">
                      <IconButton size="small" aria-label={`Move ${WEEKDAY_FULL[i]} ${dateOf(i)} to somebody else`} disabled={busy} onClick={(e) => setMenu({ anchor: e.currentTarget, date: day.date })} sx={actionSx}>
                        <ForwardIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              )}
            </Stack>
            {(day?.movedFrom || gone) && (
              <Stack direction="row" alignItems="center" gap={0.5} sx={{ pl: '74px', mt: 0.25 }}>
                <Typography variant="caption" color="text.secondary" noWrap sx={{ minWidth: 0 }}>
                  {day?.movedFrom ? `Under ${day.movedFrom.name || 'another'}'s ID` : `Driven by ${nameOf(gone!.line)}, ${gone!.day.stops} stops`}
                </Typography>
                {!locked && (
                  <Button
                    size="small"
                    disabled={busy}
                    onClick={() => (day?.movedFrom
                      ? onMove(line._id, day.date, day.movedFrom.lineId)
                      : onMove(gone!.line._id, gone!.day.date, line._id))}
                    sx={{ py: 0, px: 0.5, minWidth: 0, fontSize: 12 }}
                  >
                    {day?.movedFrom ? 'Send back' : 'Undo'}
                  </Button>
                )}
              </Stack>
            )}
          </Box>
        );
      })}
      <Menu anchorEl={menu?.anchor} open={!!menu} onClose={() => setMenu(null)} slotProps={{ paper: { sx: { maxHeight: 360, minWidth: 220 } } }}>
        <ListSubheader sx={{ lineHeight: '32px' }}>Who drove {menu ? WEEKDAY_FULL[indexOf(menu.date)] : ''}?</ListSubheader>
        {candidates.map((l) => (
          <MenuItem key={l._id} dense onClick={() => { const m = menu!; setMenu(null); onMove(line._id, m.date, l._id); }}>
            {nameOf(l)}
          </MenuItem>
        ))}
        {menu && candidates.length === 0 && <MenuItem dense disabled>Nobody else is free that day</MenuItem>}
      </Menu>
    </Stack>
  );
};

// One of the week's facts: a number over a word. When editing, the
// number is an input in the same type at the same place, with a hairline
// under it as the only sign it can be typed in.
const Fact: React.FC<{
  label: string;
  value: number | null;
  editing: boolean;
  typed?: boolean;
  statement?: number;
  step?: number;
  onCommit: (v: number | null) => void;
  onReset?: () => void;
}> = ({ label, value, editing, typed, statement, step = 1, onCommit, onReset }) => {
  const [draft, setDraft] = useState(value === null ? '' : String(value));
  useEffect(() => { setDraft(value === null ? '' : String(value)); }, [value, editing]);
  const commit = () => {
    const next = draft.trim() === '' ? null : Number(draft);
    if (next !== null && !Number.isFinite(next)) { setDraft(value === null ? '' : String(value)); return; }
    if (next !== value) onCommit(next);
  };
  const numberSx = { fontWeight: 700, fontSize: 20, lineHeight: 1.2, fontVariantNumeric: 'tabular-nums', color: typed ? 'primary.dark' : 'text.primary' } as const;
  return (
    <Box sx={{ minWidth: 0, flex: 1 }}>
      {editing ? (
        <Box
          component="input"
          aria-label={label}
          inputMode="decimal"
          step={step}
          value={draft}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
          sx={{
            ...numberSx, fontFamily: 'inherit', width: '100%', maxWidth: 88, p: 0, m: 0, border: 0, outline: 0, bgcolor: 'transparent',
            borderBottom: '2px solid', borderColor: 'primary.main', borderRadius: 0,
          }}
        />
      ) : (
        <Typography sx={{ ...numberSx, borderBottom: '2px solid transparent' }}>{value === null ? '—' : count(value)}</Typography>
      )}
      <Stack direction="row" alignItems="center" gap={0.25}>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        {typed && onReset && (
          <Tooltip title={`Back to the statement${statement !== undefined ? ` (${count(statement)})` : ''}`} placement="top">
            <IconButton size="small" aria-label={`Back to the statement ${label}`} onClick={onReset} sx={{ p: 0.25, color: 'text.disabled', '&:hover': { color: 'primary.main' } }}>
              <UndoIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
    </Box>
  );
};

// The person's note for every week, pinned above this week's note. It
// lives on the pay profile, so it follows them from week to week until
// it is unpinned (which turns it back into this week's note).
const StandingNote: React.FC<{
  text: string;
  locked: boolean;
  busy: boolean;
  onSave: (text: string) => void;
  onUnpin: () => void;
}> = ({ text, locked, busy, onSave, onUnpin }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(text);
  useEffect(() => { setDraft(text); setEditing(false); }, [text]);
  return (
    <Box sx={{ mb: 1.5, p: 1.25, borderRadius: 2, border: '1px solid', borderColor: (t) => alpha(t.palette.warning.main, 0.35), bgcolor: (t) => alpha(t.palette.warning.main, 0.08) }}>
      <Stack direction="row" alignItems="center" gap={0.75} sx={{ mb: 0.5 }}>
        <PinIcon sx={{ fontSize: 14, color: 'warning.dark' }} />
        <Typography variant="caption" sx={{ fontWeight: 700, color: 'warning.dark', flex: 1 }}>Every week</Typography>
        {!locked && !editing && (
          <>
            <Tooltip title="Edit">
              <IconButton size="small" aria-label="Edit the note for every week" disabled={busy} onClick={() => setEditing(true)} sx={{ p: 0.25 }}><EditIcon sx={{ fontSize: 16 }} /></IconButton>
            </Tooltip>
            <Tooltip title="Unpin: keep it on this week only">
              <IconButton size="small" aria-label="Unpin the note" disabled={busy} onClick={onUnpin} sx={{ p: 0.25 }}><PinOutlineIcon sx={{ fontSize: 16 }} /></IconButton>
            </Tooltip>
          </>
        )}
      </Stack>
      {editing ? (
        <TextField
          size="small"
          fullWidth
          multiline
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => { setEditing(false); if (draft.trim() !== text) onSave(draft.trim()); }}
          onKeyDown={(e) => { if (e.key === 'Escape') { setDraft(text); setEditing(false); } }}
        />
      ) : (
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{text}</Typography>
      )}
    </Box>
  );
};

const LineInspector: React.FC<{
  run: PayrollRun;
  line: PayrollLine;
  people?: { accountId: string; name: string }[];
  /** Everyone on the week, so a day can be given to one of them. */
  weekLines?: PayrollLine[];
  onChange: (line: PayrollLine, totals: RunTotals) => void;
  onMoved?: (lines: PayrollLine[], totals: RunTotals) => void;
  onRemoved?: (lineId: string, totals: RunTotals) => void;
  onClose: () => void;
}> = ({ run, line, people = [], weekLines = [], onChange, onMoved, onRemoved, onClose }) => {
  const locked = run.status === 'finalized';
  const [editing, setEditing] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [daysOpen, setDaysOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [adding, setAdding] = useState(false);
  const [adjKind, setAdjKind] = useState<AdjustmentKind>('bonus');
  const [adjAmount, setAdjAmount] = useState('');
  const [adjNote, setAdjNote] = useState('');
  const [note, setNote] = useState(line.note);
  const [assignTo, setAssignTo] = useState('');

  useEffect(() => { setNote(line.note); setAdding(false); setAdjAmount(''); setAdjNote(''); setAssignTo(''); }, [line._id, line.note]);
  useEffect(() => { setEditing(false); setRemoving(false); }, [line._id]);

  const patch = async (input: Parameters<typeof PayrollService.updateLine>[2]) => {
    setSaving(true);
    setError(null);
    try {
      const result = await PayrollService.updateLine(run._id, line._id, input);
      onChange(result.line, result.totals);
      return true;
    } catch (err) {
      setError(describeError(err, 'That change could not be saved.'));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const moveDay = async (fromLineId: string, date: string, toLineId: string) => {
    setSaving(true);
    setError(null);
    try {
      const result = await PayrollService.moveDay(run._id, fromLineId, date, toLineId);
      onMoved?.(result.lines, result.totals);
    } catch (err) {
      setError(describeError(err, 'That day could not be moved.'));
    } finally {
      setSaving(false);
    }
  };

  // Moving the week's note onto the person, or back.
  const pin = () => patch({ standingNote: [line.standingNote, note.trim()].filter(Boolean).join('\n'), note: '' });
  const unpin = () => patch({ standingNote: '', note: [line.standingNote, note.trim()].filter(Boolean).join('\n') });
  const canPin = !locked && !!line.accountId && !!line.profileId;
  const awayDays = weekLines.some((l) => l._id !== line._id && l.days.some((d) => d.movedFrom?.lineId === line._id));

  const remove = async () => {
    setSaving(true);
    setError(null);
    try {
      const { totals } = await PayrollService.removeLine(run._id, line._id);
      onRemoved?.(line._id, totals);
    } catch (err) {
      setError(describeError(err, 'That could not be removed.'));
    } finally {
      setSaving(false);
    }
  };

  const rules = line.rulesSnapshot.filter((r) => r.enabled !== false);
  const wants = (kind: string) => rules.some((r) => r.kind === kind);
  const dailyThreshold = useMemo(() => {
    const r = rules.find((x) => x.kind === 'over_threshold' && (x.period || 'day') === 'day' && x.threshold != null);
    return r ? Number(r.threshold) : null;
  }, [rules]);

  const tone = line.status === 'ok' ? 'ok' : line.status === 'no_rules' ? 'warn' : 'neutral';
  const skipped = line.status === 'skipped';
  const editable = !locked && (editing || !!line.custom);

  return (
    <Box
      component="aside"
      aria-label={`${line.name || line.statementName}, this week`}
      sx={{
        bgcolor: 'background.paper', border: '1px solid', borderColor: 'primary.light', borderRadius: 3.5,
        boxShadow: '0 12px 32px rgba(0, 71, 153, 0.08)', overflow: 'hidden',
      }}
    >
      <Stack direction="row" alignItems="center" gap={1.5} sx={{ px: 2.5, py: 2 }}>
        <Avatar name={line.name || line.statementName} size={40} muted={skipped} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 16 }} noWrap>{line.name || line.statementName}</Typography>
          <Stack direction="row" alignItems="center" gap={1} sx={{ mt: 0.25 }}>
            <ToneChip tone={tone} label={line.custom ? 'Added by hand' : STATUS_LABEL[line.status]} hollow={line.status !== 'no_rules'} />
            {line.accountId && (
              <Button size="small" component={RouterLink} to={`/payroll/people/${line.accountId}`} endIcon={<OpenIcon sx={{ fontSize: 14 }} />} sx={{ py: 0, px: 0.75, minWidth: 0, color: 'text.secondary', fontWeight: 500 }}>
                Pay rules
              </Button>
            )}
          </Stack>
        </Box>
        <IconButton size="small" aria-label="Close" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
      </Stack>

      {error && <Alert severity="error" sx={{ mx: 2.5, mb: 1 }} onClose={() => setError(null)}>{error}</Alert>}

      {skipped ? (
        <Box sx={{ px: 2.5, py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Nobody chosen for this row.
          </Typography>
          <Stack direction="row" gap={1}>
            <Select size="small" displayEmpty value={assignTo} onChange={(e) => setAssignTo(String(e.target.value))} sx={{ flex: 1 }} disabled={locked}
              renderValue={(v) => v ? people.find((p) => p.accountId === v)?.name : <Typography component="span" color="text.secondary">Who is this?</Typography>}>
              {people.map((p) => <MenuItem key={p.accountId} value={p.accountId}>{p.name}</MenuItem>)}
            </Select>
            <Button variant="contained" disableElevation disabled={!assignTo || saving || locked} onClick={() => patch({ accountId: assignTo })}>Assign</Button>
          </Stack>
        </Box>
      ) : (
        <>
          <Box sx={{ px: 2.5, py: 2, bgcolor: 'grey.50', borderTop: '1px solid', borderBottom: '1px solid', borderColor: 'divider' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Eyebrow>This week</Eyebrow>
              {!locked && !line.custom && (
                <Tooltip title={editing ? 'Done' : 'Edit the numbers'}>
                  <IconButton size="small" aria-label={editing ? 'Done' : 'Edit'} onClick={() => setEditing((e) => !e)} sx={{ color: editing ? 'primary.main' : 'text.secondary' }}>
                    {editing ? <DoneIcon fontSize="small" /> : <EditIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
            {/* Days, stops and packages come from the statement and open
                with the pencil; training, hours and vacation never do, so
                they are typed straight in. */}
            <Stack direction="row" gap={1.5} alignItems="flex-start">
              <Fact label={line.totals.days === 1 ? 'day' : 'days'} value={line.totals.days} editing={editable} typed={line.totals.daysTyped} statement={line.custom ? undefined : line.totals.statementDays ?? line.days.length} onCommit={(v) => patch({ inputs: { daysWorked: v } })} onReset={() => patch({ inputs: { daysWorked: null } })} />
              <Fact label="stops" value={line.totals.stops} editing={editable} typed={line.totals.stopsTyped} statement={line.custom ? undefined : line.totals.statementStops} onCommit={(v) => patch({ inputs: { stops: v } })} onReset={() => patch({ inputs: { stops: null } })} />
              <Fact label="packages" value={line.totals.packages} editing={editable} typed={line.totals.packagesTyped} statement={line.custom ? undefined : line.totals.statementPackages} onCommit={(v) => patch({ inputs: { packages: v } })} onReset={() => patch({ inputs: { packages: null } })} />
              {(wants('training_day') || line.inputs.trainingDays > 0) && (
                <Fact label="training" value={line.inputs.trainingDays} editing={!locked} onCommit={(v) => patch({ inputs: { trainingDays: v ?? 0 } })} />
              )}
              {(wants('hourly') || line.inputs.hours !== null) && (
                <Fact label="hours" value={line.inputs.hours} editing={!locked} step={0.25} onCommit={(v) => patch({ inputs: { hours: v } })} />
              )}
              {(wants('vacation_day') || line.inputs.vacationDays > 0) && (
                <Fact label="vacation" value={line.inputs.vacationDays} editing={!locked} onCommit={(v) => patch({ inputs: { vacationDays: v ?? 0 } })} />
              )}
            </Stack>
          </Box>

          <Box sx={{ px: 2.5, pt: 1.5, pb: 2 }}>
            <Eyebrow>Calculation</Eyebrow>
            <Receipt
              line={line}
              locked={locked}
              onRemove={(id) => patch({ removeAdjustmentId: id })}
              onSkipRule={(ruleId, skip) => patch(skip ? { skipRule: ruleId } : { restoreRule: ruleId })}
            />
            {line.status === 'no_rules' && line.accountId && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                No pay rules yet. <Link component={RouterLink} to={`/payroll/people/${line.accountId}`}>Set up pay</Link>
              </Alert>
            )}
            {!locked && (
              <>
                <Collapse in={adding}>
                  <Stack gap={1} sx={{ mt: 1.5, p: 1.5, bgcolor: 'grey.50', borderRadius: 2 }}>
                    <Stack direction="row" gap={1}>
                      <Select size="small" value={adjKind} onChange={(e) => setAdjKind(e.target.value as AdjustmentKind)} sx={{ flex: 1 }}>
                        {(Object.keys(ADJUSTMENT_LABEL) as AdjustmentKind[]).map((k) => <MenuItem key={k} value={k}>{ADJUSTMENT_LABEL[k]}</MenuItem>)}
                      </Select>
                      <TextField size="small" placeholder="0.00" value={adjAmount} onChange={(e) => setAdjAmount(e.target.value)} inputProps={{ inputMode: 'decimal', style: { textAlign: 'right', fontVariantNumeric: 'tabular-nums' } }} sx={{ width: 110 }} InputProps={{ startAdornment: <Typography color="text.secondary" sx={{ mr: 0.5 }}>$</Typography> }} />
                    </Stack>
                    <TextField size="small" placeholder="Why (optional)" value={adjNote} onChange={(e) => setAdjNote(e.target.value)} />
                    <Stack direction="row" justifyContent="flex-end" gap={1}>
                      <Button size="small" onClick={() => setAdding(false)}>Cancel</Button>
                      <Button size="small" variant="contained" disableElevation disabled={saving || !Number(adjAmount)} onClick={async () => {
                        const ok = await patch({ addAdjustment: { kind: adjKind, amount: Number(adjAmount), note: adjNote } });
                        if (ok) { setAdding(false); setAdjAmount(''); setAdjNote(''); }
                      }}>
                        Add
                      </Button>
                    </Stack>
                  </Stack>
                </Collapse>
                {!adding && (
                  <Button size="small" startIcon={<AddIcon />} onClick={() => setAdding(true)} sx={{ mt: 0.5, color: 'text.secondary', px: 0.5 }}>
                    Add adjustment
                  </Button>
                )}
              </>
            )}
            <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mt: 1.5, pt: 1.5, borderTop: '2px solid', borderColor: 'grey.900' }}>
              <Typography variant="body2" color="text.secondary">Pay this week</Typography>
              <Typography sx={{ fontSize: 26, fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.1, fontVariantNumeric: 'tabular-nums', color: line.status === 'no_rules' ? 'text.disabled' : 'primary.dark' }}>
                {money(line.total, { cents: true })}
              </Typography>
            </Stack>
          </Box>

          {(line.days.length > 0 || awayDays) && (
            <Box sx={{ px: 2.5, py: 1, borderTop: '1px solid', borderColor: 'divider' }}>
              <Stack
                component="button"
                type="button"
                aria-expanded={daysOpen}
                onClick={() => setDaysOpen((o) => !o)}
                direction="row" justifyContent="space-between" alignItems="center"
                sx={{ width: '100%', py: 0.75, px: 0, border: 0, bgcolor: 'transparent', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit' }}
              >
                <Eyebrow>Stops by day</Eyebrow>
                <Stack direction="row" alignItems="center" gap={0.5}>
                  {dailyThreshold !== null && <Typography variant="caption" color="text.secondary">line at {dailyThreshold}</Typography>}
                  <ExpandIcon fontSize="small" sx={{ color: 'text.secondary', transform: daysOpen ? 'rotate(180deg)' : 'none', transition: 'transform 150ms' }} />
                </Stack>
              </Stack>
              <Collapse in={daysOpen}>
                <Box sx={{ pt: 0.5, pb: 1 }}>
                  <DayRows line={line} weekLines={weekLines} weekStart={run.weekStart} threshold={dailyThreshold} locked={locked || !onMoved} busy={saving} onMove={moveDay} />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.25 }}>
                    Pickup {count(line.totals.pickupStops)} · delivery {count(line.totals.deliveryStops)} · eCommerce {count(line.totals.ecommerceStops)}
                  </Typography>
                  {line.statementName && line.name && line.statementName !== line.name && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      On the statement as "{line.statementName}".
                    </Typography>
                  )}
                </Box>
              </Collapse>
            </Box>
          )}

          <Box sx={{ px: 2.5, py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            {line.standingNote && (
              <StandingNote text={line.standingNote} locked={locked} busy={saving} onSave={(text) => patch({ standingNote: text })} onUnpin={unpin} />
            )}
            <TextField
              size="small"
              fullWidth
              multiline
              label="Note for this week"
              value={note}
              disabled={locked}
              onChange={(e) => setNote(e.target.value)}
              onBlur={(e) => {
                // Pressing the pin blurs the field first; let the pin save it.
                if ((e.relatedTarget as HTMLElement | null)?.dataset?.pin) return;
                if (note !== line.note) patch({ note });
              }}
              InputProps={{
                endAdornment: canPin && note.trim() ? (
                  <InputAdornment position="end" sx={{ alignSelf: 'flex-start', mt: 1.25 }}>
                    <Tooltip title="Pin: show on every week">
                      <IconButton size="small" edge="end" data-pin="1" aria-label="Pin the note to every week" disabled={saving} onClick={pin}>
                        <PinOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ) : undefined,
              }}
            />
            {locked && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Finalized. Reopen to edit.
              </Typography>
            )}
            {!locked && line.days.length === 0 && (line.custom || line.status === 'absent' || line.status === 'no_rules') && onRemoved && (
              removing ? (
                <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1} sx={{ mt: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">Take {line.name} off this week?</Typography>
                  <Stack direction="row" gap={0.5}>
                    <Button size="small" onClick={() => setRemoving(false)}>Keep</Button>
                    <Button size="small" color="error" variant="contained" disableElevation disabled={saving} onClick={remove}>Remove</Button>
                  </Stack>
                </Stack>
              ) : (
                <Button size="small" startIcon={<RemoveIcon fontSize="small" />} onClick={() => setRemoving(true)} sx={{ mt: 1, color: 'text.secondary' }}>
                  Remove from this week
                </Button>
              )
            )}
          </Box>
        </>
      )}
    </Box>
  );
};

export default LineInspector;
